import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import React from "react";
import ProgressBar from "../ui/ProgressBar";
import { API_BASE, ADMIN_POLL_INTERVAL, fetchJson } from "../utils";
import { validateJob } from "../api/jobs";
import { describeApiError } from "../api/request";

// Mounted exclusively inside the authenticated admin hub.
export default function LatestBpmJob() {
  const [job, setJob] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [revision, setRevision] = React.useState(0);
  React.useEffect(() => {
    let timer;
    let failures = 0;
    let active = true;
    let pending = false;
    const controller = new AbortController();
    const poll = async () => {
      if (!active || document.hidden || pending) return;
      pending = true;
      clearTimeout(timer);
      try {
        const data = validateJob(
          await fetchJson(`${API_BASE}/admin/tracks/fetch-bpm/`, {
            signal: controller.signal,
          }),
          "bpm",
        );
        if (!active) return;
        failures = 0;
        setJob(data);
        setError(null);
        if (["started", "running", "stopping"].includes(data.status))
          timer = setTimeout(poll, ADMIN_POLL_INTERVAL);
      } catch (failure) {
        if (!active || failure.name === "AbortError") return;
        setError(failure);
        if (!failure.status || failure.status >= 500) {
          timer = setTimeout(
            poll,
            Math.min(30000, ADMIN_POLL_INTERVAL * 2 ** ++failures),
          );
        }
      } finally {
        pending = false;
      }
    };
    poll();
    document.addEventListener("visibilitychange", poll);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
      document.removeEventListener("visibilitychange", poll);
    };
  }, [revision]);
  return (
    <section aria-labelledby="latest-bpm-title">
      <h3 id="latest-bpm-title">Latest BPM job</h3>
      <p>
        This is the most recently created job, including automatic import
        batches. It does not track all enrichment or a specific user's
        collection.
      </p>
      {error && (
        <Alert role="alert" severity="error">
          {describeApiError(error)}
        </Alert>
      )}
      <p role="status">
        {job ? `Latest job: ${job.status}` : "Loading latest job…"}
        {job?.items_updated !== undefined &&
          ` · ${job.items_updated} tracks changed (BPM or duration)`}
      </p>
      {job?.status === "running" && job.import_total > 0 && (
        <ProgressBar
          label="Latest job progress"
          value={job.progress / job.import_total}
        />
      )}
      <Button className="btn" onClick={() => setRevision((value) => value + 1)}>
        Refresh latest job
      </Button>
      <p>
        A completed job may leave missing metadata. Manual retries reuse lookup
        caches; stopping one job does not cancel the queue.
      </p>
    </section>
  );
}
